import { ref, watch, onUnmounted } from 'vue';
/**
 * 가격 추세 감지 composable
 *
 * @param price - 감지할 가격 (Ref 또는 ComputedRef)
 * @param options - 옵션 설정
 * @returns 가격 추세 상태 및 관련 함수
 *
 * @example
 * ```ts
 * // 기본 사용 (지속적인 추세)
 * const { trend } = usePriceTrend(buyPrice);
 *
 * // 플래시 효과 사용 (임시 효과)
 * const { trend, flashState } = usePriceTrend(sellPrice, {
 *   enableFlash: true,
 *   flashDuration: 300
 * });
 * ```
 */
export function usePriceTrend(price, options = {}) {
    const { enableFlash = false, flashDuration = 300 } = options;
    // 이전 가격 추적
    const previousPrice = ref(0);
    // 가격 추세 상태 (최초에는 중립)
    const trend = ref('trending-neutral');
    // 플래시 상태 (플래시 효과 사용 시에만 사용)
    const flashState = ref('');
    // 플래시 타이머 ID
    let flashTimeoutId = null;
    // 가격 변화 감지
    watch(price, (newPrice) => {
        // 유효하지 않은 가격이면 무시
        if (!newPrice || newPrice === 0) {
            // 가격이 0이거나 유효하지 않으면 이전 가격도 초기화하지 않음
            // (이전 가격은 유지하여 다음 유효한 가격과 비교 가능하도록)
            return;
        }
        // 이전 가격이 없으면 현재 가격을 이전 가격으로 설정하고 최초에는 중립 상태
        if (previousPrice.value === 0) {
            previousPrice.value = newPrice;
            trend.value = 'trending-neutral'; // 최초에는 중립
            return;
        }
        // 가격 비교 및 추세 상태 업데이트
        if (newPrice > previousPrice.value) {
            trend.value = 'trending-up'; // 상승
            // 플래시 효과 사용 시
            if (enableFlash) {
                flashState.value = 'up';
                resetFlashState();
            }
        }
        else if (newPrice < previousPrice.value) {
            trend.value = 'trending-down'; // 하락
            // 플래시 효과 사용 시
            if (enableFlash) {
                flashState.value = 'down';
                resetFlashState();
            }
        }
        // 가격이 변경되지 않았으면 이전 추세 유지 (중립으로 변경하지 않음)
        // 비교 후 이전 가격 업데이트 (다음 비교를 위해)
        previousPrice.value = newPrice;
    }, { immediate: true } // immediate: true로 설정하여 초기값도 처리
    );
    /**
     * 플래시 상태 초기화 (지정된 시간 후)
     */
    function resetFlashState() {
        // 이전 타이머 취소
        if (flashTimeoutId !== null) {
            clearTimeout(flashTimeoutId);
            flashTimeoutId = null;
        }
        // 지정된 시간 후 플래시 효과 제거
        flashTimeoutId = window.setTimeout(() => {
            flashState.value = '';
            flashTimeoutId = null;
        }, flashDuration);
    }
    // 컴포넌트 언마운트 시 타이머 정리
    onUnmounted(() => {
        if (flashTimeoutId !== null) {
            clearTimeout(flashTimeoutId);
            flashTimeoutId = null;
        }
    });
    const result = {
        trend,
        previousPrice,
    };
    if (enableFlash) {
        result.flashState = flashState;
    }
    return result;
}
//# sourceMappingURL=usePriceTrend.js.map